import {
  CarouselData,
  CarouselItemData,
  SelectedData,
  WorkDetailSimpleData,
  WorkSimpleWithDetails,
} from '../dto/EntityRepository';
import { UpdateCarouselItemReq } from '../dto/ReqDtoRepository';
import {
  DisplaySize,
  FormationHorizontalAlignment,
  FormationImageMode,
  FormationVerticalAlignment,
} from '../enum/EnumRepository';
import {
  buildFormationSlotWorkDetailSnapshot,
  buildFormationSlotWorkSnapshot,
  formatFormationItemInfo,
  getFormationItemSource,
  resolveFormationSlotImage,
  type FormationItemSource,
  type ResolvedFormationSlotImage,
} from './formationUtils';

export const CAROUSEL_MAX_ITEMS = 10;
/** 슬롯의 최대 가로:세로. 이보다 납작한 이미지는 슬롯 높이를 이미지에 맞춘다. */
export const CAROUSEL_SLOT_ASPECT_RATIO = '5 / 3';
/** 모바일 슬롯은 슬라이드와 관계없이 3:4로 고정한다. */
export const CAROUSEL_MOBILE_SLOT_ASPECT_RATIO = '3 / 4';

/** maxAspect보다 가로가 긴 이미지는 위아래 여백이 없도록 슬롯 비율을 이미지에 맞춘다. */
export function getCarouselSlotAspectRatio(
  width: number,
  height: number,
  maxAspect = CAROUSEL_SLOT_ASPECT_RATIO,
): string {
  const [maxWidth, maxHeight] = maxAspect.split('/').map((part) => Number(part.trim()));
  const maxRatio = maxWidth > 0 && maxHeight > 0 ? maxWidth / maxHeight : 5 / 3;
  if (!(width > 0) || !(height > 0) || width / height <= maxRatio) {
    return maxAspect;
  }
  return `${width} / ${height}`;
}

export const CAROUSEL_SLOT_WIDTH_PERCENT: Record<DisplaySize, number> = {
  [DisplaySize.SMALL]: 50,
  [DisplaySize.REGULAR]: 65,
  [DisplaySize.MEDIUM]: 75,
  [DisplaySize.BIG]: 85,
  [DisplaySize.WIDE]: 100,
};

const PENDING_CAROUSEL_ITEM_ID_PREFIX = 'pending-carousel-item-';
let pendingCarouselItemSeq = 0;

export interface CarouselImportSelection {
  selected: SelectedData;
  work?: WorkSimpleWithDetails;
  detail?: WorkDetailSimpleData;
}

function emptyUploadFile() {
  return {
    id: '',
    originUrl: '',
    allowDownload: false,
    width: 0,
    height: 0,
  };
}

function asFormationItem(item: CarouselItemData) {
  return {
    ...item,
    focusX: 0.5,
    focusY: 0.5,
    imageMode: FormationImageMode.FIT,
    horizontalAlignment: FormationHorizontalAlignment.CENTER,
    verticalAlignment: FormationVerticalAlignment.CENTER,
  };
}

export function isPendingCarouselItemId(id: string | number): boolean {
  return typeof id === 'string' && id.startsWith(PENDING_CAROUSEL_ITEM_ID_PREFIX);
}

export function createEmptyCarouselItem(slotIndex: number): CarouselItemData {
  pendingCarouselItemSeq += 1;
  return {
    id: `${PENDING_CAROUSEL_ITEM_ID_PREFIX}${pendingCarouselItemSeq}`,
    slotIndex,
    work: null,
    workDetail: null,
    uploadFile: emptyUploadFile(),
    caption: '',
  };
}

export function getCarouselItemSource(item: CarouselItemData): FormationItemSource {
  return getFormationItemSource(asFormationItem(item));
}

export function resolveCarouselSlotImage(
  item: CarouselItemData,
): ResolvedFormationSlotImage | null {
  const resolved = resolveFormationSlotImage(asFormationItem(item));
  if (!resolved) return null;
  if (getCarouselItemSource(item) !== 'directUpload') return resolved;
  return {
    ...resolved,
    alt: item.caption?.trim() || 'Carousel',
  };
}

export function formatCarouselItemInfo(
  item: CarouselItemData,
  includeArtist = false,
): string {
  return formatFormationItemInfo(asFormationItem(item), includeArtist);
}

export function getFilledCarouselItems(items: CarouselItemData[]): CarouselItemData[] {
  return [...items]
    .filter((item) => getCarouselItemSource(item) !== 'empty')
    .sort((a, b) => a.slotIndex - b.slotIndex);
}

export function hasFilledCarouselSlide(items: CarouselItemData[]): boolean {
  return getFilledCarouselItems(items).length > 0;
}

export function getCarouselImportCapacity(items: CarouselItemData[]): number {
  return Math.max(0, CAROUSEL_MAX_ITEMS - getFilledCarouselItems(items).length);
}

export function applyCarouselItemOrder(
  items: CarouselItemData[],
  orderedIds: string[],
): CarouselItemData[] {
  const filled = getFilledCarouselItems(items);
  const byId = new Map(filled.map((item) => [String(item.id), item]));
  const used = new Set<string>();
  const ordered: CarouselItemData[] = [];
  orderedIds.forEach((id) => {
    const key = String(id);
    const item = byId.get(key);
    if (!item || used.has(key)) return;
    used.add(key);
    ordered.push(item);
  });
  filled.forEach((item) => {
    if (!used.has(String(item.id))) ordered.push(item);
  });
  const next = reindexCarouselItems(ordered);
  return next.length > 0 ? next : [createEmptyCarouselItem(0)];
}

function reindexCarouselItems(items: CarouselItemData[]): CarouselItemData[] {
  return items.map((item, slotIndex) => (
    item.slotIndex === slotIndex ? item : { ...item, slotIndex }
  ));
}

function withCarouselUploadFile(item: CarouselItemData): CarouselItemData {
  return {
    ...item,
    work: item.work ?? null,
    workDetail: item.workDetail ?? null,
    uploadFile: item.uploadFile ?? emptyUploadFile(),
  };
}

export function normalizeCarouselData(carousel: CarouselData | null | undefined): CarouselData {
  const items = (carousel?.items ?? []).map(withCarouselUploadFile);
  const filled = getFilledCarouselItems(items).slice(0, CAROUSEL_MAX_ITEMS);
  if (filled.length === 0) {
    const existingEmpty = items.find((item) => getCarouselItemSource(item) === 'empty');
    return {
      id: carousel?.id ?? '',
      items: [existingEmpty
        ? { ...existingEmpty, slotIndex: 0 }
        : createEmptyCarouselItem(0)],
    };
  }
  return {
    id: carousel?.id ?? '',
    items: reindexCarouselItems(filled),
  };
}

export function applyCarouselSlotReference(
  item: CarouselItemData,
  selected: SelectedData,
  work?: WorkSimpleWithDetails,
  detail?: WorkDetailSimpleData,
): CarouselItemData {
  const cleared: CarouselItemData = {
    ...item,
    uploadFile: emptyUploadFile(),
    caption: '',
    referenceChanged: true,
    imageChanged: false,
    captionChanged: false,
  };

  if (selected.detailId) {
    return {
      ...cleared,
      work: null,
      workDetail: buildFormationSlotWorkDetailSnapshot(selected, detail),
    };
  }

  return {
    ...cleared,
    work: buildFormationSlotWorkSnapshot(selected, work),
    workDetail: null,
  };
}

export function applyCarouselImports(
  items: CarouselItemData[],
  selections: CarouselImportSelection[],
): { items: CarouselItemData[]; omittedCount: number } {
  const filled = getFilledCarouselItems(items);
  const capacity = Math.max(0, CAROUSEL_MAX_ITEMS - filled.length);
  const accepted = selections.slice(0, capacity);
  const appended = accepted.map((selection) => applyCarouselSlotReference(
    createEmptyCarouselItem(0),
    selection.selected,
    selection.work,
    selection.detail,
  ));
  const next = reindexCarouselItems([...filled, ...appended]);

  return {
    items: next.length > 0 ? next : [createEmptyCarouselItem(0)],
    omittedCount: Math.max(0, selections.length - capacity),
  };
}

export function replaceCarouselSlot(
  items: CarouselItemData[],
  slotIndex: number,
  selected: SelectedData,
  work?: WorkSimpleWithDetails,
  detail?: WorkDetailSimpleData,
): CarouselItemData[] {
  return items.map((item) => (
    item.slotIndex === slotIndex
      ? applyCarouselSlotReference(item, selected, work, detail)
      : item
  ));
}

export function applyCarouselDirectUpload(
  item: CarouselItemData,
  originUrl: string,
  width: number,
  height: number,
  caption: string,
): CarouselItemData {
  return {
    ...item,
    work: null,
    workDetail: null,
    uploadFile: {
      id: item.uploadFile.id,
      originUrl,
      allowDownload: false,
      width,
      height,
    },
    caption,
    imageChanged: true,
    captionChanged: (item.caption ?? '') !== caption,
    referenceChanged: false,
  };
}

export function removeCarouselItemsByIds(
  items: CarouselItemData[],
  ids: string[],
): CarouselItemData[] {
  const idSet = new Set(ids);
  let next = items;
  items.filter((item) => idSet.has(String(item.id))).forEach((target) => {
    const current = next.find((item) => item.id === target.id);
    if (!current) return;
    next = removeCarouselSlot(next, current.slotIndex);
  });
  return next;
}

export function removeCarouselSlot(
  items: CarouselItemData[],
  slotIndex: number,
): CarouselItemData[] {
  const target = items.find((item) => item.slotIndex === slotIndex);
  if (!target) return items;
  if (
    getCarouselItemSource(target) === 'empty'
    && !hasFilledCarouselSlide(items)
  ) {
    return items;
  }

  const remaining = reindexCarouselItems(
    items.filter((item) => (
      item.slotIndex !== slotIndex && getCarouselItemSource(item) !== 'empty'
    )),
  );
  return remaining.length > 0 ? remaining : [createEmptyCarouselItem(0)];
}

export function convertCarouselItemsToUpdateReq(
  items: CarouselItemData[],
): UpdateCarouselItemReq[] {
  return items.map((item) => {
    const identity = isPendingCarouselItemId(item.id) ? {} : { id: item.id };
    const base = { ...identity, slotIndex: item.slotIndex };
    const source = getCarouselItemSource(item);

    if (source === 'detail' && item.workDetail?.id) {
      return { ...base, workDetailId: item.workDetail.id, caption: '' };
    }
    if (source === 'work' && item.work?.id) {
      return { ...base, workId: item.work.id, caption: '' };
    }
    if (source === 'directUpload') {
      return {
        ...base,
        caption: item.caption ?? '',
        updateUploadFileReq: {
          ...(item.uploadFile.id ? { uploadFileId: item.uploadFile.id } : {}),
          originUrl: item.uploadFile.originUrl,
          allowDownload: item.uploadFile.allowDownload,
          width: item.uploadFile.width,
          height: item.uploadFile.height,
        },
      };
    }
    return base;
  });
}
